import { type Locale } from "@/constants/locales";
import { Routes } from "@/constants/routes";

type SchemaType = {
    "@context": "https://schema.org";
    "@type": string;
    [key: string]: any;
};


export class SchemaGenerator {
    constructor(
        private route: typeof Routes[keyof typeof Routes],
        private locale: Locale
    ) { }

    getUrl(url: string) {
        return `${process.env.NEXT_PUBLIC_SITE_URL}${url}`
    }

    generateWebsiteSchema(): SchemaType {
        const href = this.route.href[this.locale];
        const title = this.route.meta.title[this.locale];
        const description = this.route.meta.description[this.locale];

        return {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "url": this.getUrl(href),
            "name": title,
            "description": description,
            "publisher": {
                "@type": "Organization",
                "name": "FakeXGenerator",
                "logo": {
                    "@type": "ImageObject",
                    "url": "https://fakexgenerator.com/logo.png"
                }
            }
        };
    }

    generateOrganizationSchema(): SchemaType {
        return {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "FakeXGenerator",
            "url": this.getUrl("/"),
            "logo": {
                "@type": "ImageObject",
                "url": "https://fakexgenerator.com/logo.png"
            },
            "description": this.route.meta.description[this.locale],
        };
    }

    generateWebPageSchema(): SchemaType {
        const title = this.route.meta.title[this.locale];
        const description = this.route.meta.description[this.locale];

        return {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "url": this.getUrl(this.route.href[this.locale]),
            "name": title,
            "description": description,
            "inLanguage": this.locale,
            "publisher": {
                "@type": "Organization",
                "name": "FakeXGenerator",
                "logo": {
                    "@type": "ImageObject",
                    "url": "https://fakexgenerator.com/logo.png"
                }
            }
        };
    }

    generateSoftwareApplicationSchema(): SchemaType {
        return {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "FakeXGenerator",
            "url": this.getUrl(this.route.href[this.locale]),
            "applicationCategory": "EducationalApplication",
            "browserRequirements": "Requires JavaScript. Requires HTML5.",
            "operatingSystem": "All",
            "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD"
            },
            "description": this.route.meta.description[this.locale],
            "featureList": (this.route as any).schema.data.messages[this.locale],
        };
    }

    generateBlogPageSchema(): SchemaType {
        return {
            "@context": "https://schema.org",
            "@type": "Blog",
            "url": this.getUrl(this.route.href[this.locale]),
            "name": this.route.meta.title[this.locale],
            "description": this.route.meta.description[this.locale],
            "inLanguage": this.locale,
            "publisher": {
                "@type": "Organization",
                "name": "FakeXGenerator",
                "logo": {
                    "@type": "ImageObject",
                    "url": "https://fakexgenerator.com/logo.png"
                }
            }
        };
    }

    generateBlogPostSchema(): SchemaType {
        const postData = (this.route as any).postData;

        return {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": this.route.meta.title[this.locale],
            "description": this.route.meta.description[this.locale],
            "url": this.getUrl(this.route.href[this.locale]),
            "datePublished": new Date(postData.date).toISOString(),
            "dateModified": new Date(postData.date).toISOString(),
            "inLanguage": this.locale,
            "publisher": {
                "@type": "Organization",
                "name": "FakeXGenerator",
                "logo": {
                    "@type": "ImageObject",
                    "url": "https://fakexgenerator.com/logo.png"
                }
            },
            "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": this.getUrl(this.route.href[this.locale])
            }
        };
    }

    generateFAQSchema(faqs: Array<{ question: string; answer: string }>): SchemaType {
        return {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map((faq) => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": faq.answer
                }
            }))
        };
    }

    generateBreadcrumbSchema(breadcrumbs: Array<{ name: string; href: string }>): SchemaType {
        return {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": breadcrumbs.map((crumb, index) => ({
                "@type": "ListItem",
                "position": index + 1,
                "name": crumb.name,
                "item": this.getUrl(crumb.href)
            }))
        };
    }
}